import { API_CONFIG, API_ENDPOINTS } from '../helpers/constants';

const API_BASE_URL = API_CONFIG.API_BASE_URL;

// Create Zoho subscription request: plan ($0 plan), customer (like planUpgradeService), company_id, platform_name from env
export interface CreateZohoSubscriptionRequest {
  plan: {
    name: string;
    planCode: string;
  };
  customer: {
    displayName: string;
    email: string;
    companyName: string;
  };
  company_id: string;
  platform_name: string;
}

// Create Zoho subscription response
export interface CreateZohoSubscriptionResponse {
  success: boolean;
  message?: string;
  code?: number;
  data?: unknown;
}

/**
 * Create Zoho subscription for the company (e.g. after email verification during onboarding).
 * Payload: plan (name, planCode from $0 plan), customer (displayName, email, companyName), company_id, platform_name from env.
 * @param data - Request payload with plan, customer, company_id, platform_name
 * @param refreshToken - Bearer refresh token (same as used for create hosted page)
 * @returns Promise with create subscription response
 */
export const createZohoSubscription = async (
  data: CreateZohoSubscriptionRequest,
  refreshToken: string
): Promise<CreateZohoSubscriptionResponse> => {
  try {
    // Request body: plan, customer, company_id, platform_name (no redirectUrl)
    const requestBody = {
      plan: data.plan,
      customer: data.customer,
      company_id: data.company_id,
      platform_name: data.platform_name,
    };
    const response = await fetch(`${API_BASE_URL}${API_ENDPOINTS.PAYMENT.ZOHO.CREATE}`, {
      method: 'POST',
      headers: {
        accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${refreshToken}`,
      },
      body: JSON.stringify(requestBody),
    });

    const responseData = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(responseData.message || responseData.detail || `Create subscription failed: ${response.status}`);
    }
    return {
      success: responseData.success !== false,
      message: responseData.message,
      code: responseData.code,
      data: responseData.data,
    };
  } catch (error) {
    console.error('Create Zoho subscription error:', error);
    throw error;
  }
};

// Hosted Page Request Interface - matches the exact API specification
export interface HostedPageRequest {
  plan: {
    name: string;
    planCode: string;
  };
  redirectUrl: string;
  customer: {
    displayName: string;
    email: string;
    companyName: string;
  };
}

// Hosted Page Update Request - for upgrade to paid plans (hostedPage/update API)
export interface HostedPageUpdateRequest {
  subscriptionId: string;
  plan: {
    name: string;
    planCode: string;
  };
  addons?: string[];
  referenceId?: string;
  startsAt?: string;
  customFields?: string[];
  couponCode?: string;
  redirectUrl: string;
  salespersonName?: string;
  canChargeSetupFeeImmediately?: boolean;
  exchangeRate?: number;
  placeOfSupply?: string;
  gstTreatment?: string;
  gstNo?: string;
  cfdiUsage?: string;
  paymentGateways?: string[];
  billingAddressId?: string;
  shippingAddressId?: string;
  branchId?: string;
  templateId?: number;
  platform_name: string;
}

// Hosted Page Response Interface - matches the exact API response
export interface HostedPageResponse {
  success: boolean;
  data: {
    hostedPageId: string;
    url: string;
    planId: string;
    planName: string;
    amount: number;
    companyId: string;
    companyName: string;
    expiresAt: string;
  };
  message: string;
  code: number;
}

// Payment Status Response Interface
export interface PaymentStatusResponse {
  success: boolean;
  status: string;
  message: string;
  code: number;
  data?: any;
}

/**
 * Create a hosted payment page
 * @param data - Hosted page request data
 * @param authToken - Authentication token
 * @returns Promise with hosted page response
 */
export const createHostedPaymentPage = async (
  data: HostedPageRequest,
  authToken: string
): Promise<HostedPageResponse> => {
  try {
    const requestBody = {
      ...data,
      platform_name: 'prism7',
    };
    const response = await fetch(`${API_BASE_URL}${API_ENDPOINTS.PAYMENT.ZOHO.CREATE_HOSTED_PAGE}`, {
      method: 'POST',
      headers: {
        'accept': 'application/json',
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${authToken}`,
      },
      body: JSON.stringify(requestBody),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `Failed to create hosted page: ${response.status}`);
    }

    const responseData = await response.json();
    return responseData;
  } catch (error) {
    console.error('Create hosted page error:', error);
    throw error;
  }
};

/**
 * Create a hosted payment page for plan upgrade (calls hostedPage/update).
 * Used when upgrading to paid plans; requires existing subscriptionId.
 * @param data - Hosted page update request (subscriptionId, plan { name, planCode }, redirectUrl, platform_name required)
 * @param authToken - Authentication token
 * @returns Promise with hosted page response (url, hostedPageId, etc.)
 */
export const createHostedPaymentPageForUpgrade = async (
  data: HostedPageUpdateRequest,
  authToken: string
): Promise<HostedPageResponse> => {
  try {
    // Build body with only defined fields so API receives clean payload; plan.name and plan.planCode are required
    const requestBody: HostedPageUpdateRequest = {
      subscriptionId: data.subscriptionId,
      plan: { name: data.plan.name, planCode: data.plan.planCode },
      redirectUrl: data.redirectUrl,
      platform_name: data.platform_name,
    };
    // Optional fields: only include when provided
    if (data.addons?.length) requestBody.addons = data.addons;
    if (data.referenceId != null) requestBody.referenceId = data.referenceId;
    if (data.startsAt != null) requestBody.startsAt = data.startsAt;
    if (data.customFields?.length) requestBody.customFields = data.customFields;
    if (data.couponCode != null) requestBody.couponCode = data.couponCode;
    if (data.salespersonName != null) requestBody.salespersonName = data.salespersonName;
    if (data.canChargeSetupFeeImmediately != null) requestBody.canChargeSetupFeeImmediately = data.canChargeSetupFeeImmediately;
    if (data.exchangeRate != null) requestBody.exchangeRate = data.exchangeRate;
    if (data.placeOfSupply != null) requestBody.placeOfSupply = data.placeOfSupply;
    if (data.gstTreatment != null) requestBody.gstTreatment = data.gstTreatment;
    if (data.gstNo != null) requestBody.gstNo = data.gstNo;
    if (data.cfdiUsage != null) requestBody.cfdiUsage = data.cfdiUsage;
    if (data.paymentGateways?.length) requestBody.paymentGateways = data.paymentGateways;
    if (data.billingAddressId != null) requestBody.billingAddressId = data.billingAddressId;
    if (data.shippingAddressId != null) requestBody.shippingAddressId = data.shippingAddressId;
    if (data.branchId != null) requestBody.branchId = data.branchId;
    if (data.templateId != null) requestBody.templateId = data.templateId;

    // hostedPage/update expects PUT (405 Method Not Allowed when using POST)
    const response = await fetch(`${API_BASE_URL}${API_ENDPOINTS.PAYMENT.ZOHO.HOSTED_PAGE_UPDATE}`, {
      method: 'PUT',
      headers: {
        accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${authToken}`,
      },
      body: JSON.stringify(requestBody),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `Failed to create hosted page for upgrade: ${response.status}`);
    }

    const responseData = await response.json();
    // Normalize response: update API may use different shape (e.g. no top-level success, or url in data.hosted_page_url)
    const resData = responseData?.data ?? responseData;
    const url =
      resData?.url ??
      resData?.hosted_page_url ??
      resData?.hostedPageUrl ??
      responseData?.url ??
      responseData?.hosted_page_url ??
      responseData?.hostedPageUrl;
    return {
      success: responseData?.success !== false,
      data: { ...(typeof resData === 'object' && resData !== null ? resData : {}), url },
      message: responseData?.message ?? 'OK',
      code: responseData?.code ?? 200,
    };
  } catch (error) {
    console.error('Create hosted page for upgrade error:', error);
    throw error;
  }
};

// Validate payment request payload for /validatePayment API
export interface ValidatePaymentRequest {
  hostedPageId: string;
  subscriptionId: string;
  companyId: string;
  userId: string;
  platform_name: string;
}

/**
 * Verify payment by calling validatePayment API (replaces fetchByHostedId).
 * @param payload - Request payload: hostedPageId, subscriptionId, companyId, userId, platform_name
 * @param authToken - Authentication token
 * @returns Promise with payment status response (success, data, message, code)
 */
export const verifyPaymentStatus = async (
  payload: ValidatePaymentRequest,
  authToken: string
): Promise<PaymentStatusResponse> => {
  try {
    const response = await fetch(`${API_BASE_URL}${API_ENDPOINTS.PAYMENT.ZOHO.VALIDATE_PAYMENT}`, {
      method: 'POST',
      headers: {
        accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${authToken}`,
      },
      body: JSON.stringify({
        hostedPageId: payload.hostedPageId,
        subscriptionId: payload.subscriptionId,
        companyId: payload.companyId,
        userId: payload.userId,
        platform_name: payload.platform_name,
      }),
    });

    const responseData = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(responseData.message || `Failed to verify payment: ${response.status}`);
    }

    // Map API response { success, data, message, code } to PaymentStatusResponse
    const success = responseData.success === true;
    return {
      success,
      status: success ? 'success' : (responseData.data?.status ?? 'pending'),
      message: responseData.message ?? 'Payment status verified successfully',
      code: responseData.code ?? 200,
      data: responseData.data,
    };
  } catch (error) {
    console.error('Verify payment error:', error);
    throw error;
  }
};

// Update Company Plan Response Interface
export interface UpdateCompanyPlanResponse {
  success: boolean;
  message: string;
  code: number;
  data?: any;
}

/**
 * Update company plan after successful payment.
 * subscriptionId and zohoCustomerId should come from mySubscriptionData (zohoSubscriptionId, zohoCustomerId).
 * @param data - Company plan update data (companyId, newPlanId, subscriptionId from zohoSubscriptionId, zohoCustomerId from mySubscriptionData)
 * @param authToken - Authentication token
 * @returns Promise with update response
 */
export const updateCompanyPlan = async (
  data: {
    companyId: string;
    newPlanId: string;
    subscriptionId?: string;
    zohoCustomerId?: string;
    hostedPageId?: string;
  },
  authToken: string
): Promise<UpdateCompanyPlanResponse> => {
  try {
    const response = await fetch(`${API_BASE_URL}${API_ENDPOINTS.PAYMENT.ZOHO.UPDATE_COMPANY_PLAN}`, {
      method: 'POST',
      headers: {
        'accept': 'application/json',
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${authToken}`,
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `Failed to update company plan: ${response.status}`);
    }

    const responseData = await response.json();
    return {
      success: responseData.success || true,
      message: responseData.message || 'Company plan updated successfully',
      code: responseData.code || 200,
      data: responseData.data,
    };
  } catch (error) {
    console.error('Update company plan error:', error);
    throw error;
  }
};
